import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxButton } from 'components/RevxButton';
import { JobItem } from 'domains/workspaceDomain';
import { useUser } from 'hooks';
import { useTranslation } from 'react-i18next';
import { getCancelJobRequestUrl } from 'utils/cancelJobRequestLink';
import { useBuyerCanRequestCancel } from '../../hooks/useJobCardActions';
import './CardCancelJobBtn.less';

type Props = {
  data: JobItem;
};

/**
 * Cancel Job for a buyer, in the same spot on the card the sourcer's button sits (PRD-416).
 *
 * It looks the same on purpose but does something else entirely: a buyer cannot cancel a job, so
 * this opens a Zendesk form asking why, and a CSR runs the real cancellation from their own card.
 * Nothing about the job changes here -- there is no cancellation record to create, nothing to
 * reload, and the card is left exactly as the buyer found it.
 *
 * Shares CardCancelJobBtn's stylesheet rather than copying it, so the two stay the same size and
 * sit on the same line as the Actions box beside them.
 */
const CardBuyerCancelJobBtn = ({ data }: Props) => {
  const { t } = useTranslation();
  const user = useUser();
  const canRequestCancel = useBuyerCanRequestCancel(data);

  if (!canRequestCancel) {
    return null;
  }

  // The number the card itself prints, so the ticket quotes what the buyer is looking at. The org
  // is the one they are signed in as -- the same name Zendesk already knows them by.
  const jobNumber = data.revisionJobId || data.id;
  const orgName = user?.orgName || data.jobContacts?.createBy?.company;

  return (
    <RevxFlexLayout justify="justify-center" className="card-cancel-job">
      <RevxButton
        brandGreen
        className="card-cancel-job-btn"
        onClick={() =>
          // a new tab: the form is Zendesk's own page, and the buyer should come back to the job
          // they were looking at rather than having to navigate to it again.
          window.open(
            getCancelJobRequestUrl(jobNumber, orgName),
            '_blank',
            'noopener,noreferrer'
          )
        }
      >
        {t('workspace.tabs.moreActions.cancelJob')}
      </RevxButton>
    </RevxFlexLayout>
  );
};

export default CardBuyerCancelJobBtn;
